import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Fallback defaults
const FALLBACK_CONFIG = {
  maxPhotos: 6,
  maxFileSizeMB: 2,
  enableClientResize: true,
  clientResizeMaxWidth: 1920,
  clientResizeMaxHeight: 1080,
  clientResizeQuality: 0.85,
  totalMaxUploadMB: 12,
};

// Global cache to prevent duplicate API calls
let uploadConfigCache = null;
let uploadConfigPromise = null;

export default function useUploadConfig() {
  const [config, setConfig] = useState(FALLBACK_CONFIG);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    // Return cached data if available
    if (uploadConfigCache) {
      setConfig(uploadConfigCache);
      setLoading(false);
      return;
    }

    // If there's already a request in progress, wait for it
    if (uploadConfigPromise) {
      try {
        const result = await uploadConfigPromise;
        setConfig(result);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    uploadConfigPromise = (async () => {
      try {
        const response = await api.getUploadConfig();
        const loadedConfig = response.config || FALLBACK_CONFIG;

        // Normalize field names (handle both camelCase and snake_case from API)
        const normalizedConfig = {
          maxPhotos: loadedConfig.maxPhotos ?? loadedConfig.max_photos ?? FALLBACK_CONFIG.maxPhotos,
          maxFileSizeMB:
            loadedConfig.maxFileSizeMB ??
            loadedConfig.max_file_size_mb ??
            FALLBACK_CONFIG.maxFileSizeMB,
          enableClientResize:
            loadedConfig.enableClientResize ??
            loadedConfig.enable_client_resize ??
            FALLBACK_CONFIG.enableClientResize,
          clientResizeMaxWidth:
            loadedConfig.clientResizeMaxWidth ??
            loadedConfig.client_resize_max_width ??
            FALLBACK_CONFIG.clientResizeMaxWidth,
          clientResizeMaxHeight:
            loadedConfig.clientResizeMaxHeight ??
            loadedConfig.client_resize_max_height ??
            FALLBACK_CONFIG.clientResizeMaxHeight,
          clientResizeQuality:
            loadedConfig.clientResizeQuality ??
            loadedConfig.client_resize_quality ??
            FALLBACK_CONFIG.clientResizeQuality,
          totalMaxUploadMB:
            loadedConfig.totalMaxUploadMB ??
            loadedConfig.maxPhotos * loadedConfig.maxFileSizeMB ??
            FALLBACK_CONFIG.totalMaxUploadMB,
        };

        uploadConfigCache = normalizedConfig;
        setConfig(normalizedConfig);
        return normalizedConfig;
      } catch (err) {
        console.error('Error loading upload config:', err);
        setError(err);
        // Return fallback on error
        setConfig(FALLBACK_CONFIG);
        return FALLBACK_CONFIG;
      } finally {
        setLoading(false);
        uploadConfigPromise = null;
      }
    })();

    try {
      await uploadConfigPromise;
    } catch (err) {
      // Error already handled
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    loading,
    error,
    reloadConfig: loadConfig,
  };
}

// Function to clear cache (useful when config is updated by admin)
export function clearUploadConfigCache() {
  uploadConfigCache = null;
  uploadConfigPromise = null;
}
